// Model-number and make normalization (spec: Ingestion and matching, steps 1-2).

export interface NormalizedModel {
  raw: string;
  /** Uppercase, spaces/dashes/dots/underscores removed. */
  normalized: string;
  /** The part before a "/" variant marker (e.g. "SHX878ZD5N/23" -> "SHX878ZD5N"). */
  base: string;
  /** What followed the "/" marker, if any. */
  variant: string | null;
}

export function normalizeModel(raw: string): NormalizedModel {
  const trimmed = raw.trim();
  const upper = trimmed.toUpperCase();
  const [basePart, ...rest] = upper.split('/');
  const strip = (s: string) => s.replace(/[\s\-_.]/g, '');
  const base = strip(basePart);
  const variant = rest.length ? strip(rest.join('/')) || null : null;
  return { raw: trimmed, normalized: variant ? `${base}/${variant}` : base, base, variant };
}

// Canonical make -> aliases. Small on purpose: the real table belongs in the catalog DB.
const MAKES: Record<string, string[]> = {
  'GE Appliances': ['ge', 'general electric', 'ge appliances', 'geappliances.com'],
  Whirlpool: ['whirlpool', 'whirlpool corporation', 'whirlpool.com'],
  Bosch: ['bosch', 'bosch home', 'bosch-home.com'],
  LG: ['lg', 'lg electronics', 'lg.com'],
  Samsung: ['samsung', 'samsung electronics', 'samsung.com'],
  Honda: ['honda', 'honda power equipment', 'powerequipment.honda.com'],
  DeWalt: ['dewalt', 'de walt', 'dewalt.com'],
  Frigidaire: ['frigidaire', 'electrolux frigidaire', 'frigidaire.com'],
  Maytag: ['maytag', 'maytag.com'],
  'Demo Appliance Co': ['demo', 'demo appliance co', 'demo appliance'],
};

const ALIAS_INDEX = new Map<string, string>();
for (const [canonical, aliases] of Object.entries(MAKES)) {
  ALIAS_INDEX.set(canonical.toLowerCase(), canonical);
  for (const a of aliases) ALIAS_INDEX.set(a, canonical);
}

/** Map an alias or domain to the canonical make; unknown makes come back trimmed, as typed. */
export function resolveMake(raw: string): { canonical: string; known: boolean } {
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  const hit = ALIAS_INDEX.get(key);
  return hit ? { canonical: hit, known: true } : { canonical: raw.trim(), known: false };
}

const ALIASES_LONGEST_FIRST = [...ALIAS_INDEX.keys()].filter((a) => !a.includes('.')).sort((a, b) => b.length - a.length);

/** First known make named anywhere in free text (whole words, longest alias wins), else null. */
export function findMake(text: string): string | null {
  const hay = ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, ' ')} `;
  for (const alias of ALIASES_LONGEST_FIRST) {
    if (hay.includes(` ${alias.replace(/[^a-z0-9]+/g, ' ').trim()} `)) return ALIAS_INDEX.get(alias)!;
  }
  return null;
}
